<template>
  <div class="px-4 sm:px-0">
  <PageTitle
    :title="$gettext('Clients')"
    :description="$gettext('Voici un résumé de l\'ensemble des clients.')" />
  <div class="mt-8 flow-root">
    <div v-if="customerStore.totalCount == 0 && !customerStore.searchQuery" class="text-center p-20">
      <div class="flex justify-center items-center">
        <NewCustomerIcon />
      </div>
      <h3 class="mt-2 text-sm font-semibold text-gray-900">{{ $gettext("Pas encore de clients") }}</h3>
      <p class="mt-1 text-sm text-gray-500">{{ $gettext("Désolé, mais nous ne trouvons pas de clients... Veuillez ajouter un nouveau client.") }}</p>
      <div class="mt-6">
        <router-link :to="{name: 'new-company'}"
          class="inline-flex items-center rounded-md bg-indigo-600 m-5 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <PlusIcon class="-ml-0.5 mr-1.5 size-5 rounded-md border border-white-900" aria-hidden="true" />
          {{ $gettext("Nouvelle organisation") }}
        </router-link>
        <router-link :to="{name: 'new-physical-person'}"
          class="inline-flex items-center rounded-md bg-indigo-600 m-5 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <PlusIcon class="-ml-0.5 mr-1.5 size-5 rounded-md border border-white-900" aria-hidden="true" />
          {{ $gettext("Nouvelle personne physique") }}
        </router-link>
      </div>
    </div>
    <div v-else>
      <div class="flex justify-between items-center mb-4">
        <div class="relative w-96">
          <MagnifyingGlassIcon class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 size-5" />
          <input v-model="customerStore.searchQuery" type="text" :placeholder="$gettext('Chercher un client...')"
            class="w-full pl-10 p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>
        <div>
          <router-link :to="{name: 'new-company'}"
            class="mr-5 inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            <PlusIcon class="-ml-0.5 mr-1.5 size-5 rounded-md border border-white-900" aria-hidden="true" />
            {{ $gettext("Nouvelle organisation") }}
          </router-link>
          <router-link :to="{name: 'new-physical-person'}"
            class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            <PlusIcon class="-ml-0.5 mr-1.5 size-5 rounded-md border border-white-900" aria-hidden="true" />
            {{ $gettext("Nouvelle personne physique") }}
          </router-link>
        </div>
      </div>
      <div class="mt-8">
        <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table v-if="customerStore.customers.length > 0" class="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">{{ $gettext("Nom") }}</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $gettext("Profil de risque") }}</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $gettext("Vente(s)") }}</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $gettext("Location(s)") }}</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"></th>
                  <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-0"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr
                  v-for="customer in customerStore.paginatedCustomers"
                  :key="customer.id"
                  class="even:bg-gray-50 hover:bg-indigo-50 transition-colors duration-300 cursor-pointer"
                  @click="$router.push({ name: customer.type === 'company' ? 'company' : 'physical-person', params: { id: customer.id } })"
                >
                  <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 gap-2">
                    <!-- prevent row click when clicking customer link -->
                    <CustomerLink :customer="customer" class="pl-4" @click.stop />
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <RiskScoringLabel :level="customer.relationships?.lastRiskScoring?.data?.attributes?.scoreLevel ?? undefined" />
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ customer.relationships?.saleListingAgreements?.count ?? customer.relationships?.saleListingAgreements?.data?.length }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ customer.relationships?.rentalListingAgreements?.count ?? customer.relationships?.rentalListingAgreements?.data?.length }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <span v-if="customer.attributes?.isUboOnly || false" class="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-inset ring-gray-500/10">{{ $gettext("UBO uniquement") }}</span>
                  </td>
                  <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                    <div class="flex justify-end space-x-2 pr-4">
                      <!-- prevent row click when clicking action links -->
                      <router-link
                        class="text-indigo-600 hover:text-indigo-900 transition-transform duration-200 hover:scale-110"
                        :to="{ name: customer.type === 'company' ? 'company' : 'physical-person', params: { id: customer.id } }"
                        @click.stop
                        v-if="!(customer.attributes?.isUboOnly || false)"
                      >
                        <MagnifyingGlassIcon class="size-5" aria-hidden="true" />
                      </router-link>

                      <router-link
                        class="text-indigo-600 hover:text-indigo-900 transition-transform duration-200 hover:scale-110"
                        :to="{ name: customer.type === 'company' ? 'edit-company' : 'edit-physical-person', params: { id: customer.id } }"
                        @click.stop
                      >
                        <PencilSquareIcon class="size-5" aria-hidden="true" />
                      </router-link>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="text-center py-20">
              <h3 class="mt-2 text-sm font-semibold text-gray-900">{{ $gettext("Pas de résultats trouvés") }}</h3>
            </div>
          </div>
        </div>
      </div>
      <PaginationNavigation :store="customerStore" />
    </div>
  </div>
</div>
</template>

<script setup>
import { onMounted } from "vue";
import NewCustomerIcon from "@/components/assets/NewCustomerIcon.vue";
import { PlusIcon } from "@heroicons/vue/20/solid";
import { MagnifyingGlassIcon, PencilSquareIcon } from "@heroicons/vue/24/outline";
import { useCustomerStore } from "@/stores/customer.js";
import PaginationNavigation from "@/components/layout/PaginationNavigation.vue";
import RiskScoringLabel from "@/components/risk-scorings/RiskScoringLabel.vue";
import CustomerLink from "@/components/customers/CustomerLink.vue";
import PageTitle from "@/components/layout/PageTitle.vue";

const customerStore = useCustomerStore();

const customerOverviewIncludes = ["lastRiskScoringLevel", "saleListingAgreementsCount", "rentalListingAgreementsCount"];

onMounted(async () => {
  await customerStore.fetchAll(null, customerOverviewIncludes);
  await customerStore.watchSourceChanges(() => {
    customerStore.fetchAll(null, customerOverviewIncludes);
  });
});
</script>
